import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Eye, Loader2, ChevronLeft, ChevronRight, Images, Sparkles, PenTool, Upload } from 'lucide-react';
import { Button, Modal } from '../ui';
import { morningPagesService, getAssetUrl, type MorningPageDto } from '../../services';
import './PhotoGallery.css';

interface PhotoGalleryProps {
  onPhotoClick?: (page: MorningPageDto) => void;
  onDeleteClick?: (page: MorningPageDto) => void;
  onUploadClick?: () => void;
  showDelete?: boolean;
  /** Change this value to trigger a data refresh */
  refreshKey?: number;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  onPhotoClick,
  onDeleteClick,
  onUploadClick,
  showDelete = true,
  refreshKey = 0,
}) => {
  const [pages, setPages] = useState<MorningPageDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<MorningPageDto | null>(null);
  const [modalPhotoIndex, setModalPhotoIndex] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 12;

  const loadPages = useCallback(async (page: number, append = false) => {
    try {
      setIsLoading(true);
      const response = await morningPagesService.getMorningPages(page, pageSize);

      if (append) {
        setPages((prev) => [...prev, ...response.items]);
      } else {
        setPages(response.items);
      }

      setHasMore(response.items.length === pageSize);
      setError(null);
    } catch (err) {
      setError('Failed to load morning pages');
    } finally {
      setIsLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    setPageNumber(1);
    loadPages(1);
  }, [loadPages, refreshKey]);

  const handleLoadMore = () => {
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    loadPages(nextPage, true);
  };

  const handleDelete = async (page: MorningPageDto) => {
    if (!window.confirm('Are you sure you want to delete this morning page?')) {
      return;
    }

    try {
      await morningPagesService.deleteMorningPage(page.id);
      setPages((prev) => prev.filter((p) => p.id !== page.id));
      onDeleteClick?.(page);
    } catch (err) {
      setError('Failed to delete morning page');
    }
  };

  const handlePhotoClick = (page: MorningPageDto) => {
    setSelectedPage(page);
    setModalPhotoIndex(0);
    onPhotoClick?.(page);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateLong = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
  };

  if (error && pages.length === 0) {
    return (
      <div className="photo-gallery__error">
        <p>{error}</p>
        <Button onClick={() => loadPages(1)}>Try Again</Button>
      </div>
    );
  }

  if (!isLoading && pages.length === 0) {
    return (
      <div className="photo-gallery__empty">
        <div className="photo-gallery__empty-icon">
          <PenTool size={32} />
        </div>
        <h3>Start your journal</h3>
        <p>Capture your morning pages to unlock AI-powered insights about your creative journey.</p>
        {onUploadClick && (
          <Button variant="primary" size="md" onClick={onUploadClick} className="photo-gallery__empty-cta">
            <Upload size={18} />
            Upload Your First Page
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="photo-gallery">
      <div className="photo-gallery__grid">
        {pages.map((page, index) => (
          <div
            key={page.id}
            className="photo-gallery__card"
            style={{ '--card-index': index } as React.CSSProperties}
          >
            <div
              className="photo-gallery__image-wrapper"
              onClick={() => handlePhotoClick(page)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handlePhotoClick(page)}
            >
              <img
                src={getAssetUrl(page.thumbnailUrl || page.photoUrl)}
                alt={`Morning page from ${formatDate(page.date)}`}
                className="photo-gallery__image"
                loading="lazy"
              />
              <div className="photo-gallery__overlay">
                <Eye size={22} />
                <span>View</span>
              </div>
              {/* Badges */}
              <div className="photo-gallery__badges">
                {page.photoCount > 1 && (
                  <span className="photo-gallery__badge photo-gallery__badge--count">
                    <Images size={12} />
                    {page.photoCount}
                  </span>
                )}
                {page.hasAnalysis && (
                  <span className="photo-gallery__badge photo-gallery__badge--analyzed">
                    <Sparkles size={12} />
                  </span>
                )}
              </div>
            </div>
            <div className="photo-gallery__info">
              <div className="photo-gallery__date-row">
                <span className="photo-gallery__date">
                  {formatDate(page.date)}
                </span>
                {showDelete && (
                  <button
                    className="photo-gallery__delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(page);
                    }}
                    aria-label="Delete morning page"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {isLoading && (
        <div className="photo-gallery__loading">
          <Loader2 size={24} className="photo-gallery__spinner" />
          <span>Loading...</span>
        </div>
      )}

      {hasMore && !isLoading && (
        <div className="photo-gallery__load-more">
          <Button variant="secondary" onClick={handleLoadMore}>
            Load More
          </Button>
        </div>
      )}

      {selectedPage && (() => {
        const photos = selectedPage.photoUrls?.length > 0
          ? selectedPage.photoUrls
          : [selectedPage.photoUrl];
        const currentPhoto = photos[modalPhotoIndex] || photos[0];

        return (
          <Modal
            isOpen={!!selectedPage}
            onClose={() => setSelectedPage(null)}
            title={`${formatDateLong(selectedPage.date)}${photos.length > 1 ? ` (${modalPhotoIndex + 1}/${photos.length})` : ''}`}
            size="lg"
          >
            <div className="photo-gallery__modal">
              <div className="photo-gallery__modal-image-container">
                <img
                  src={getAssetUrl(currentPhoto)}
                  alt={`Morning page from ${formatDate(selectedPage.date)} - page ${modalPhotoIndex + 1}`}
                  className="photo-gallery__modal-image"
                />
                {photos.length > 1 && (
                  <>
                    <button
                      className="photo-gallery__modal-nav photo-gallery__modal-nav--prev"
                      onClick={() => setModalPhotoIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1))}
                      aria-label="Previous photo"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      className="photo-gallery__modal-nav photo-gallery__modal-nav--next"
                      onClick={() => setModalPhotoIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0))}
                      aria-label="Next photo"
                    >
                      <ChevronRight size={24} />
                    </button>
                    <div className="photo-gallery__modal-dots">
                      {photos.map((_, i) => (
                        <button
                          key={i}
                          className={`photo-gallery__modal-dot ${i === modalPhotoIndex ? 'photo-gallery__modal-dot--active' : ''}`}
                          onClick={() => setModalPhotoIndex(i)}
                          aria-label={`Go to page ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {selectedPage.hasOcrText && selectedPage.ocrText && (
                <div className="photo-gallery__ocr">
                  <h4>Extracted Text</h4>
                  <p>{selectedPage.ocrText}</p>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}
    </div>
  );
};

export default PhotoGallery;
